// What differs between the message displays and layouts (#141, #154), as one object each (chats/ChatsMessage.tsx for
// bubbles, classic/ClassicMessage.tsx for cozy and compact, irc/IrcMessage.tsx for irc). MessageList picks the object in ONE
// place and the shared row asks it for the pieces below; everything else a row shows (the content,
// attachments, previews, reactions, lists, threads, the hover actions, the menus) is shared.
import type { ComponentType, ReactNode } from "react";
import type { Attachment } from "@bloom/shared";
import type { ChatMessage } from "../realtime.ts";
import type { OnOpen } from "./Attachments.tsx";

/** One message as the list draws it, with what decides its look (and so its height). */
export interface RowItem {
  m: ChatMessage;
  /** Starts a run (the grouping rule: same author, within 7 minutes, no reply, no quiet line between). */
  first: boolean;
  /** Ends a run. */
  last: boolean;
  /** The first message of a calendar day. */
  newDay: boolean;
  /** 15 minutes or more since the message before it (or a new day). */
  stamp: boolean;
  /** The first unread message: the NEW line sits above it. */
  unread: boolean;
  /** Lists it's in that I can see (badges under it). */
  badges: number;
  /** Key for its measured height: the same message drawn differently (now starting a group) is measured anew. */
  hkey: string;
}

/** What a layout's frame draws around a message's content. */
export interface FrameProps {
  it: RowItem;
  name: string;
  /** I wrote it. */
  mine: boolean;
  /** Not sent yet, still streaming, deleted or being edited: no time beside it. */
  quiet: boolean;
  /** Settings → Appearance → Message display is compact. */
  compact: boolean;
  /** A 1:1 DM: the other person's name isn't repeated. */
  oneToOne: boolean;
  /** The "AI" and "BOT" tags for the sender's name. */
  tags: ReactNode;
  /** The quote of the message this one replies to, or null. */
  replyRef: ReactNode;
  /** The message's content (text, cards, attachments, previews, reactions, thread link). */
  children: ReactNode;
}

/** What a layout's own attachments (MessageLayout.Attachments) are given: the same as the shared component. */
export interface AttachmentsSlot {
  messageId: string;
  attachments: Attachment[];
  onOpen: OnOpen;
  /** Removes one for everyone (only for the author, owners and admins). */
  onRemove?: (ref: string) => void;
}

export interface MessageLayout {
  id: "bubbles" | "classic" | "irc";
  /** The line above a message after a pause or on a new day (null: none). */
  Stamp: ComponentType<{ it: RowItem }>;
  /** Extra classes on the `.msg` element. */
  rowClass: (it: RowItem, mine: boolean, editing: boolean) => string;
  /** The row around the content: avatar or gutter, the sender's name, the time. */
  Frame: ComponentType<FrameProps>;
  /** Optional (IRC, #154): draws attachments its own way (a link per file) in place of the shared cards. */
  Attachments?: ComponentType<AttachmentsSlot>;
  /** Optional (IRC): the text a message shows, from its stored body ("/me waves" shows "waves"). */
  bodyText?: (body: string) => string;
  /** Optional (IRC): wraps a quiet system line (adds the time before it). */
  System?: ComponentType<{ it: RowItem; children: ReactNode }>;
  /** Wraps a message's text: a bubble (Chats) or nothing (Classic). `bare`: only emoji, no bubble. */
  Bubble: ComponentType<{ bare?: boolean; children: ReactNode }>;
  // ---- Height guesses (MessageList's `estimate`) and the key a measured height is stored under ----
  /** Height of the line `Stamp` draws above this row, or 0. */
  stampPx: (it: RowItem) => number;
  /** The room above the first line of a message: the name line and the gap before a run. */
  headPx: (it: RowItem, compact: boolean) => number;
  /** A reply's quote line. */
  replyPx: number;
  /** A text body of this many lines at this list width. */
  textPx: (body: string, width: number) => number;
  /** The part of the height key that this layout's look depends on. */
  key: (it: RowItem) => string;
}

const dayFmt = new Intl.DateTimeFormat(undefined, { weekday: "long", month: "long", day: "numeric" });

/** "Today", "Yesterday" or "Wednesday, October 21". */
export function dayLabel(ts: number): string {
  const today = new Date().toDateString();
  const yesterday = new Date(Date.now() - 86400_000).toDateString();
  const k = new Date(ts).toDateString();
  return k === today ? "Today" : k === yesterday ? "Yesterday" : dayFmt.format(ts);
}

/** A day line's height, for placing the NEW line in a row that starts with one. */
export const DAY_PX = 34;
